import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert, Col, Row, Form } from '@themesberg/react-bootstrap';
import { END_POINT_PRODUCT_CATEGORIES, END_POINT_PRINCIPAL } from '../const';
import { showSuccessToast } from '../utils/toast';
import EditFormLayout from '../components/EditFormLayout';
import RecordEditFeedback from '../components/RecordEditFeedback';
import RecordEditActions from '../components/RecordEditActions';
import { FormikSearchableSelect, buildSelectOptions } from '../components/SearchableSelect';
import useRecordEditor from '../hooks/useRecordEditor';

const RELATED_ENDPOINTS = { principals: END_POINT_PRINCIPAL };

export const ProductCategoryEditForm = ({ productCategoryId, onSuccess, onCancel, onSubmittingChange }) => {
  const editor = useRecordEditor({
    recordId: productCategoryId, endpoint: END_POINT_PRODUCT_CATEGORIES, label: 'product category',
    relatedEndpoints: RELATED_ENDPOINTS, onSuccess, onSubmittingChange,
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editor.record?.name || '',
      principal_id: editor.record?.principal_id?.toString() || '',
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required('Product name is required'),
      principal_id: Yup.string().notOneOf(['0'], 'Please select a valid Principal').required('Principal is required'),
    }),
    onSubmit: editor.submit,
  });
  const principalOptions = useMemo(() => buildSelectOptions(editor.related.principals || []), [editor.related.principals]);

  if (editor.loading || editor.loadError || !editor.record) {
    return <RecordEditFeedback loading={editor.loading} error={editor.loadError}
      label="product category" onRetry={editor.retry} onCancel={onCancel} />;
  }

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <Row>
        <Col sm={6} className="mb-3">
          <Form.Group controlId="edit-product-category-principal">
            <Form.Label>Principal <span className="text-danger">*</span></Form.Label>
            <FormikSearchableSelect formik={formik} name="principal_id" inputId="edit-product-category-principal"
              options={principalOptions} placeholder="Search principal..." isDisabled={formik.isSubmitting}
              menuPortalTarget={onCancel ? null : undefined} />
          </Form.Group>
        </Col>
        <Col sm={6} className="mb-3">
          <Form.Group controlId="edit-product-category-name">
            <Form.Label>Product Category Name <span className="text-danger">*</span></Form.Label>
            <Form.Control type="text" disabled={formik.isSubmitting}
              {...formik.getFieldProps('name')}
              isInvalid={Boolean(formik.touched.name && formik.errors.name)} />
            <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <RecordEditActions formik={formik} backTo="/product" onCancel={onCancel} />
    </Form>
  );
};

export const EditProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleSuccess = result => {
    showSuccessToast(result.message || 'Product category updated successfully.');
    navigate('/product');
  };
  return (
    <EditFormLayout title="Edit Product Category" subtitle="Update product category and principal" backTo="/product">
      <ProductCategoryEditForm key={id} productCategoryId={id} onSuccess={handleSuccess} />
    </EditFormLayout>
  );
};
